/**
 * The battlefield. UI_UX section 7, BLUEPRINT section 19.
 *
 * This is the only component that reads `legalActions`. Everything below it
 * receives decisions as props, which is what keeps the rule knowledge in one
 * place and lets a row or a card frame stay a pure renderer.
 *
 * The interaction model has four modes:
 *   idle        clicking a hand card plays it, or opens targeting
 *   targeting   pick targets slot by slot, with an arrow that follows the cursor
 *   attackers   toggle attackers, then confirm
 *   blockers    pick a blocker, then the attacker it stops, then confirm
 *
 * Engine prompts (`pendingChoices`) sit above all four as modals, because the
 * game genuinely cannot continue until they are answered.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  canAfford,
  canBlock,
  legalAttackers,
  profile,
  slotSpecs,
  targetOptions,
  type Action,
  type BlockAssignment,
  type GameState,
} from '@/engine';
import { botDelay, useSettings } from '@/app/stores';
import { UiIcon } from '@/ui/icons/UiIcon';
import { Button, CopyReplayButton, SettingsDrawer, useIsCompact, useToast } from '@/ui/system';
import { AbilityMenu, type AbilityOption } from './AbilityMenu';
import { ArrowLayer, type ArrowSpec } from './ArrowLayer';
import { BoardRow, Hand, OpponentHand, type CardInteraction, type ZoomHandlers } from './BoardRows';
import { GasRow, PlayerBar } from './BattleBars';
import { CenterStrip } from './CenterStrip';
import { GameLog, LogToggle } from './GameLog';
import { GameOverScreen } from './GameOverScreen';
import { ChoicePrompt } from './Prompts';
import { ZoomPanel } from './ZoomPanel';
import { localPendingChoice, primaryVerb } from './interaction';
import { BOT_PLAYER, LOCAL_PLAYER, type MatchConfig, type MatchController } from './matchController';
import { useBoardSnapshot, useMatchController } from './useMatch';
import type { BoardController } from './boardController';
import styles from './Battlefield.module.css';

// ---------------------------------------------------------------------------
// Interaction state
// ---------------------------------------------------------------------------

interface TargetStep {
  label: string;
  legal: string[];
  count: number;
  optional: boolean;
}

interface Targeting {
  source: string;
  origin: 'card' | 'ability';
  abilityIndex?: number;
  costChoices?: string[];
  steps: TargetStep[];
  picks: string[][];
  index: number;
}

type Mode =
  | { kind: 'idle' }
  | { kind: 'targeting'; targeting: Targeting }
  | { kind: 'attackers'; picked: string[] }
  | { kind: 'blockers'; assignments: BlockAssignment[]; selected: string | null };

export interface BattlefieldProps {
  config: MatchConfig;
  /** Where "Back to Play" goes, and what a rematch reloads. */
  onRematch: () => void;
  onExit: () => void;
}

export interface BoardProps {
  /** The controller driving this board. */
  controller: BoardController;
  /** The seat the board is showing. Everything is from this player's side. */
  localPlayer: string;
  opponentPlayer: string;
  /**
   * `match` records the result and offers a rematch on game over. `lab` does
   * neither: a lab game is a rehearsal, and scoring it would put achievements
   * behind a dev button.
   */
  boardMode?: 'match' | 'lab';
  /** Present in match mode, for the result screen and the replay button. */
  config?: MatchConfig;
  matchController?: MatchController;
  onRematch?: () => void;
  onExit?: () => void;
  /** Extra rows in the match settings drawer. */
  settingsExtras?: ReactNode;
}

/** The standard one-human-one-bot battlefield. */
export function Battlefield({ config, onRematch, onExit }: BattlefieldProps) {
  const controller = useMatchController(config);
  return (
    <Board
      controller={controller}
      matchController={controller}
      localPlayer={LOCAL_PLAYER}
      opponentPlayer={BOT_PLAYER}
      boardMode="match"
      config={config}
      onRematch={onRematch}
      onExit={onExit}
    />
  );
}

export function Board({
  controller,
  localPlayer: LOCAL,
  opponentPlayer: FOE,
  boardMode = 'match',
  config,
  matchController,
  onRematch,
  onExit,
  settingsExtras,
}: BoardProps) {
  const snapshot = useBoardSnapshot(controller);
  const { state, log, botThinking } = snapshot;

  const toast = useToast();
  const settings = useSettings();
  const boardRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const zoom = useHoverZoom();
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Below 900px the log is a bottom sheet over the board, so it starts closed
  // and the player opens it. On desktop it is a dock and follows the setting.
  const compact = useIsCompact();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [abilityMenu, setAbilityMenu] = useState<{
    instanceId: string;
    options: AbilityOption[];
    anchor: { x: number; y: number };
  } | null>(null);

  const legal = useMemo(() => controller.localLegalActions(), [controller, state]);
  const pending = localPendingChoice(state, LOCAL);

  const me = state.players[LOCAL];
  const them = state.players[FOE];

  // --- dispatch helper ---------------------------------------------------

  const send = useCallback(
    (action: Action) => {
      // A hovered card can be unmounted by the action it is part of (a card
      // played leaves the hand), and an element that is removed never fires
      // pointerleave. Dismissing the hover zoom on every action is what stops
      // the panel from being stranded over the board.
      zoom.dismissHover();
      const result = controller.dispatch(action);
      if (!result.ok) toast.push(result.reason, 'error');
      return result.ok;
    },
    [controller, toast, zoom],
  );

  const cancel = useCallback(() => setMode({ kind: 'idle' }), []);

  // --- legal action indexes ----------------------------------------------

  const playActions = useMemo(() => {
    const map = new Map<string, Action[]>();
    for (const action of legal) {
      if (action.type !== 'PlayCard' && action.type !== 'InstallWallet') continue;
      const id = action.type === 'PlayCard' ? action.cardInstanceId : action.cardInstanceId;
      const list = map.get(id) ?? [];
      list.push(action);
      map.set(id, list);
    }
    return map;
  }, [legal]);

  const abilityActions = useMemo(() => {
    const map = new Map<string, Action[]>();
    for (const action of legal) {
      if (action.type !== 'ActivateAbility') continue;
      const list = map.get(action.instanceId) ?? [];
      list.push(action);
      map.set(action.instanceId, list);
    }
    return map;
  }, [legal]);

  const attackerPool = useMemo(
    () =>
      state.phase === 'combat' && state.activePlayer === LOCAL
        ? new Set(legalAttackers(state, LOCAL))
        : new Set<string>(),
    [state],
  );

  // --- targeting ---------------------------------------------------------

  const beginTargeting = useCallback((targeting: Targeting) => {
    setMode({ kind: 'targeting', targeting });
  }, []);

  const finishTargeting = useCallback(
    (targeting: Targeting) => {
      setMode({ kind: 'idle' });
      if (targeting.origin === 'card') {
        send({
          type: 'PlayCard',
          player: LOCAL,
          cardInstanceId: targeting.source,
          targets: targeting.picks,
        });
      } else {
        send({
          type: 'ActivateAbility',
          player: LOCAL,
          instanceId: targeting.source,
          abilityIndex: targeting.abilityIndex ?? 0,
          targets: targeting.picks,
          ...(targeting.costChoices ? { costChoices: targeting.costChoices } : {}),
        });
      }
    },
    [send],
  );

  /** Record a pick for the current step and advance when the step is full. */
  const pickTarget = useCallback(
    (id: string) => {
      if (mode.kind !== 'targeting') return;
      const t = mode.targeting;
      const step = t.steps[t.index];
      if (!step || !step.legal.includes(id)) return;

      const current = t.picks[t.index] ?? [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      const picks = t.picks.map((p, i) => (i === t.index ? next : p));

      if (next.length < Math.min(step.count, step.legal.length)) {
        setMode({ kind: 'targeting', targeting: { ...t, picks } });
        return;
      }

      const advanced = { ...t, picks, index: t.index + 1 };
      if (advanced.index >= advanced.steps.length) {
        finishTargeting(advanced);
      } else {
        setMode({ kind: 'targeting', targeting: advanced });
      }
    },
    [mode, finishTargeting],
  );

  /** Skip an optional step, or accept fewer than the maximum. */
  const advanceStep = useCallback(() => {
    if (mode.kind !== 'targeting') return;
    const t = mode.targeting;
    const advanced = { ...t, index: t.index + 1 };
    if (advanced.index >= advanced.steps.length) finishTargeting(advanced);
    else setMode({ kind: 'targeting', targeting: advanced });
  }, [mode, finishTargeting]);

  // --- playing a card from hand ------------------------------------------

  const playFromHand = useCallback(
    (instanceId: string) => {
      const actions = playActions.get(instanceId) ?? [];
      if (actions.length === 0) {
        // No toast here. An unaffordable card already shows its reason on the
        // card itself, and a card that is affordable but out of phase is dim
        // for a reason the centre strip is already stating. Toasts are reserved
        // for the engine rejecting something the player confirmed.
        return;
      }

      const install = actions.find((a) => a.type === 'InstallWallet');
      if (install) {
        send(install);
        return;
      }

      const options = targetOptions(state, LOCAL, instanceId);
      const steps: TargetStep[] = [];
      if (options.hosts.length > 0) {
        steps.push({ label: 'Pick a friendly character to attach to', legal: options.hosts, count: 1, optional: false });
      }
      for (const slot of options.slots) {
        steps.push({
          label: `Pick ${slot.count > 1 ? `${slot.count} targets` : 'a target'}`,
          legal: slot.legal,
          count: slot.count,
          optional: slot.optional,
        });
      }

      if (steps.length === 0) {
        send({ type: 'PlayCard', player: LOCAL, cardInstanceId: instanceId, targets: [] });
        return;
      }

      beginTargeting({
        source: instanceId,
        origin: 'card',
        steps,
        picks: steps.map(() => []),
        index: 0,
      });
    },
    [playActions, state, toast, send, beginTargeting],
  );

  // --- activated abilities -----------------------------------------------

  const openAbilities = useCallback(
    (instanceId: string, anchor: { x: number; y: number }) => {
      const prof = profile(state, instanceId);
      const available = abilityActions.get(instanceId) ?? [];
      const options: AbilityOption[] = [];

      prof.abilities.forEach((ability, index) => {
        if (ability.kind !== 'activated') return;
        const match = available.find((a) => a.type === 'ActivateAbility' && a.abilityIndex === index);
        options.push({
          label: ability.label ?? `Ability ${index + 1}`,
          detail: ability.speed === 'any' ? 'Any time you have priority' : 'Main phase only',
          action: match ?? {
            type: 'ActivateAbility',
            player: LOCAL,
            instanceId,
            abilityIndex: index,
          },
          blockedReason: match ? undefined : 'Not available right now.',
        });
      });

      if (options.length === 0) return false;
      if (options.length === 1 && !options[0].blockedReason) {
        activateAbility(instanceId, 0, options[0].action);
        return true;
      }
      setAbilityMenu({ instanceId, options, anchor });
      return true;
    },
    // `activateAbility` is a plain function declaration below, recreated each
    // render and only ever called from an event handler, so listing it here
    // would defeat the memo without changing behaviour.
    [state, abilityActions],
  );

  function activateAbility(instanceId: string, abilityIndex: number, fallback: Action) {
    const prof = profile(state, instanceId);
    const ability = prof.abilities[abilityIndex];
    if (!ability || ability.kind !== 'activated') {
      send(fallback);
      return;
    }
    const specs = slotSpecs(state, ability.effects, LOCAL, instanceId);
    const costChoices =
      fallback.type === 'ActivateAbility' && fallback.costChoices ? fallback.costChoices : undefined;

    if (specs.length === 0) {
      send({
        type: 'ActivateAbility',
        player: LOCAL,
        instanceId,
        abilityIndex,
        targets: [],
        ...(costChoices ? { costChoices } : {}),
      });
      return;
    }

    beginTargeting({
      source: instanceId,
      origin: 'ability',
      abilityIndex,
      costChoices,
      steps: specs.map((slot) => ({
        label: `Pick ${slot.count > 1 ? `${slot.count} targets` : 'a target'}`,
        legal: slot.legal,
        count: slot.count,
        optional: slot.optional,
      })),
      picks: specs.map(() => []),
      index: 0,
    });
  }

  // --- combat declarations -----------------------------------------------

  const confirmAttackers = useCallback(
    (picked: string[]) => {
      setMode({ kind: 'idle' });
      send({ type: 'DeclareAttackers', player: LOCAL, attackers: picked });
    },
    [send],
  );

  const confirmBlocks = useCallback(
    (assignments: BlockAssignment[]) => {
      setMode({ kind: 'idle' });
      send({ type: 'DeclareBlockers', player: LOCAL, assignments });
    },
    [send],
  );

  // --- primary action ----------------------------------------------------

  const verb = primaryVerb({
    state,
    legal,
    localPlayer: LOCAL,
    declaringAttackers: mode.kind === 'attackers',
    declaringBlockers: mode.kind === 'blockers',
    pickedCount:
      mode.kind === 'attackers' ? mode.picked.length : mode.kind === 'blockers' ? mode.assignments.length : 0,
    botThinking,
  });

  /**
   * The actions that are legal but are not the primary verb. Right now that is
   * Redraw during setup: RULES section 1 gives each player one mulligan, and
   * without a button for it the choice does not exist.
   */
  const secondaryActions = useMemo(() => {
    const out: { label: string; action: Action }[] = [];
    const redraw = legal.find((a) => a.type === 'Redraw');
    if (redraw) out.push({ label: 'Redraw', action: redraw });
    return out;
  }, [legal]);

  const onPrimary = useCallback(() => {
    switch (verb.intent.kind) {
      case 'action':
        send(verb.intent.action);
        break;
      case 'declareAttackers':
        if (mode.kind === 'attackers') confirmAttackers(mode.picked);
        else setMode({ kind: 'attackers', picked: [] });
        break;
      case 'confirmBlocks':
        if (mode.kind === 'blockers') confirmBlocks(mode.assignments);
        else setMode({ kind: 'blockers', assignments: [], selected: null });
        break;
      default:
        break;
    }
  }, [verb, mode, send, confirmAttackers, confirmBlocks]);

  // --- per-card interaction ----------------------------------------------

  const targetingStep = mode.kind === 'targeting' ? mode.targeting.steps[mode.targeting.index] : null;
  const targetingPicks = mode.kind === 'targeting' ? mode.targeting.picks[mode.targeting.index] ?? [] : [];

  const handInteraction = useCallback(
    (instanceId: string): CardInteraction => {
      if (mode.kind === 'targeting') {
        return { dimmed: instanceId !== mode.targeting.source };
      }
      const playable = (playActions.get(instanceId) ?? []).length > 0;
      const affordability = canAfford(state, LOCAL, instanceId);
      return {
        playable,
        unaffordable: !playable && !affordability.affordable,
        reason: playable ? undefined : affordability.reason,
        onClick: playable ? () => playFromHand(instanceId) : undefined,
      };
    },
    [mode, playActions, state, playFromHand],
  );

  const boardInteraction = useCallback(
    (instanceId: string, owner: 'mine' | 'theirs'): CardInteraction => {
      // Targeting: only the current step's legal ids are live.
      if (mode.kind === 'targeting') {
        const step = mode.targeting.steps[mode.targeting.index];
        const isLegal = step?.legal.includes(instanceId) === true;
        return {
          validTarget: isLegal,
          dimmed: !isLegal,
          selected: targetingPicks.includes(instanceId),
          onClick: isLegal ? () => pickTarget(instanceId) : undefined,
        };
      }

      // Attacker declaration: toggle any character that may legally attack.
      if (mode.kind === 'attackers' && owner === 'mine') {
        const canAttack = attackerPool.has(instanceId);
        return {
          validTarget: canAttack && !mode.picked.includes(instanceId),
          selected: mode.picked.includes(instanceId),
          dimmed: !canAttack,
          onClick: canAttack
            ? () =>
                setMode({
                  kind: 'attackers',
                  picked: mode.picked.includes(instanceId)
                    ? mode.picked.filter((x) => x !== instanceId)
                    : [...mode.picked, instanceId],
                })
            : undefined,
        };
      }

      // Blocker declaration: pick a blocker, then the attacker it stops.
      if (mode.kind === 'blockers') {
        if (owner === 'mine') {
          const already = mode.assignments.find((a) => a.blocker === instanceId);
          const o = state.objects[instanceId];
          const ready = o !== undefined && !o.exhausted && o.zone === 'battlefield';
          return {
            selected: mode.selected === instanceId || Boolean(already),
            validTarget: ready && mode.selected !== instanceId,
            dimmed: !ready,
            onClick: ready
              ? () =>
                  setMode({
                    kind: 'blockers',
                    assignments: already
                      ? mode.assignments.filter((a) => a.blocker !== instanceId)
                      : mode.assignments,
                    selected: mode.selected === instanceId ? null : instanceId,
                  })
              : undefined,
          };
        }
        // An enemy attacker: assign the selected blocker to it.
        const attacking = state.objects[instanceId]?.attacking === true;
        const allowed =
          attacking && mode.selected !== null && canBlock(state, mode.selected, instanceId).ok;
        return {
          validTarget: allowed,
          dimmed: !attacking,
          onClick:
            attacking && mode.selected !== null
              ? () => {
                  const check = canBlock(state, mode.selected as string, instanceId);
                  if (!check.ok) {
                    toast.push(check.reason ?? 'That character cannot block this attacker.', 'warn');
                    return;
                  }
                  setMode({
                    kind: 'blockers',
                    assignments: [
                      ...mode.assignments.filter((a) => a.blocker !== mode.selected),
                      { blocker: mode.selected as string, attacker: instanceId },
                    ],
                    selected: null,
                  });
                }
              : undefined,
        };
      }

      // Idle: a friendly permanent with an activated ability opens its menu.
      if (owner === 'mine') {
        const prof = profile(state, instanceId);
        const hasActivated = prof.abilities.some((a) => a.kind === 'activated');
        if (hasActivated) {
          return {
            playable: (abilityActions.get(instanceId) ?? []).length > 0,
            onClick: (event) => {
              const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
              openAbilities(instanceId, { x: rect.right + 8, y: rect.top });
            },
          };
        }
      }
      return {};
    },
    [mode, targetingPicks, pickTarget, attackerPool, state, abilityActions, openAbilities, toast],
  );

  // --- arrows ------------------------------------------------------------

  const arrows = useMemo<ArrowSpec[]>(() => {
    const out: ArrowSpec[] = [];

    if (mode.kind === 'targeting') {
      for (const picked of mode.targeting.picks.flat()) {
        out.push({ id: `t-${picked}`, kind: 'target', from: mode.targeting.source, to: picked });
      }
      out.push({ id: 'cursor', kind: 'target', from: mode.targeting.source });
    }

    if (mode.kind === 'attackers') {
      for (const id of mode.picked) {
        out.push({ id: `a-${id}`, kind: 'attack', from: id, to: `player:${FOE}` });
      }
    }

    // Declared attackers stay pointed at the defending player.
    for (const id of state.combat.attackers) {
      const defender = state.playerOrder.find((p) => p !== state.activePlayer);
      if (defender) out.push({ id: `da-${id}`, kind: 'attack', from: id, to: `player:${defender}` });
    }

    for (const [attacker, blockers] of Object.entries(state.combat.blockers)) {
      for (const blocker of blockers) {
        out.push({ id: `b-${blocker}`, kind: 'block', from: blocker, to: attacker });
      }
    }

    if (mode.kind === 'blockers') {
      for (const assignment of mode.assignments) {
        out.push({ id: `pb-${assignment.blocker}`, kind: 'block', from: assignment.blocker, to: assignment.attacker });
      }
    }

    return out;
  }, [mode, state]);

  // --- render ------------------------------------------------------------

  if (!me || !them) return null;

  const showLog = compact ? sheetOpen : settings.showLog;
  const underAttackPlayer = state.combat.attackers.length > 0 ? state.playerOrder.find((p) => p !== state.activePlayer) : null;
  const targetingPlayerLegal = targetingStep?.legal ?? [];

  return (
    <div
      className={styles.board}
      ref={boardRef}
      data-testid="battlefield"
      onPointerLeave={zoom.dismissHover}
    >
      <div className={styles.playArea}>
        <PlayerBar
          state={state}
          playerId={FOE}
          tutorialId="enemy-bar"
          targetable={targetingPlayerLegal.includes(FOE)}
          underAttack={underAttackPlayer === FOE}
          onAvatarClick={targetingPlayerLegal.includes(FOE) ? () => pickTarget(FOE) : undefined}
          trailing={<OpponentHand count={them.hand.length} />}
        />

        <BoardRow
          state={state}
          ids={them.battlefield.wallets}
          variant="wallets"
          label="Opponent Wallets"
          emptyText="No Wallets"
          interactionFor={(id) => boardInteraction(id, 'theirs')}
          zoomHandlers={zoom.handlers}
        />

        <BoardRow
          state={state}
          ids={[...them.battlefield.characters, ...them.battlefield.protocols]}
          variant="characters"
          label="Opponent board"
          emptyText="Opponent board is empty"
          interactionFor={(id) => boardInteraction(id, 'theirs')}
          zoomHandlers={zoom.handlers}
        />

        <CenterStrip
          state={state}
          localPlayer={LOCAL}
          verb={verb}
          onPrimary={onPrimary}
          onCancel={mode.kind === 'idle' ? undefined : cancel}
          cancelLabel={mode.kind === 'targeting' ? 'Cancel targeting' : 'Cancel'}
          secondary={mode.kind === 'idle' ? secondaryActions : undefined}
          onSecondary={send}
          botThinking={botThinking}
          onStackChipClick={zoom.pin}
        />

        <BoardRow
          state={state}
          ids={[...me.battlefield.characters, ...me.battlefield.protocols]}
          variant="characters"
          label="Your board"
          emptyText="Your board is empty"
          interactionFor={(id) => boardInteraction(id, 'mine')}
          zoomHandlers={zoom.handlers}
        />

        <BoardRow
          state={state}
          ids={me.battlefield.wallets}
          variant="wallets"
          label="Your Wallets"
          emptyText="No Wallets installed"
          interactionFor={(id) => boardInteraction(id, 'mine')}
          zoomHandlers={zoom.handlers}
        />

        <Hand state={state} playerId={LOCAL} interactionFor={handInteraction} zoomHandlers={zoom.handlers} />

        <PlayerBar
          state={state}
          playerId={LOCAL}
          tutorialId="own-bar"
          targetable={targetingPlayerLegal.includes(LOCAL)}
          underAttack={underAttackPlayer === LOCAL}
          onAvatarClick={targetingPlayerLegal.includes(LOCAL) ? () => pickTarget(LOCAL) : undefined}
          trailing={
            <>
              <GasRow state={state} playerId={LOCAL} />
              {!showLog ? (
                <LogToggle onOpen={() => (compact ? setSheetOpen(true) : settings.set('showLog', true))} />
              ) : null}
              <Button
                variant="ghost"
                size="small"
                icon={<UiIcon name="gear" size={14} />}
                onClick={() => setSettingsOpen(true)}
                aria-label="Match settings"
              >
                Menu
              </Button>
            </>
          }
        />
      </div>

      {showLog ? (
        <GameLog
          state={state}
          lines={log}
          onZoom={zoom.pin}
          onClose={() => (compact ? setSheetOpen(false) : settings.set('showLog', false))}
          footer={
            <Button
              variant="danger"
              size="small"
              block
              icon={<UiIcon name="flag" size={13} />}
              onClick={() => {
                if (window.confirm('Concede this match?')) send({ type: 'Concede', player: LOCAL });
              }}
            >
              Concede
            </Button>
          }
        />
      ) : null}

      <ArrowLayer arrows={arrows} containerRef={boardRef} followCursor={mode.kind === 'targeting'} />

      <ZoomPanel state={state} instanceId={zoom.instanceId} onClose={zoom.clear} />

      {targetingStep ? (
        <TargetingHud
          step={targetingStep}
          picked={targetingPicks}
          onSkip={advanceStep}
          onCancel={cancel}
          name={profile(state, mode.kind === 'targeting' ? mode.targeting.source : '').name}
        />
      ) : null}

      {abilityMenu ? (
        <AbilityMenu
          state={state}
          instanceId={abilityMenu.instanceId}
          options={abilityMenu.options}
          anchor={abilityMenu.anchor}
          onClose={() => setAbilityMenu(null)}
          onPick={(option) => {
            setAbilityMenu(null);
            if (option.blockedReason) {
              toast.push(option.blockedReason, 'warn');
              return;
            }
            if (option.action.type === 'ActivateAbility') {
              activateAbility(abilityMenu.instanceId, option.action.abilityIndex, option.action);
            } else {
              send(option.action);
            }
          }}
        />
      ) : null}

      {pending ? (
        <ChoicePrompt state={state} choice={pending} localPlayer={LOCAL} onAnswer={send} />
      ) : null}

      {/*
        A lab game is a rehearsal. It has no result screen and records no
        history or achievements: putting badges behind a dev button that can set
        the opponent's Integrity to zero would make them meaningless.
      */}
      {state.phase === 'over' && boardMode === 'match' && config && matchController ? (
        <GameOverScreen
          state={state}
          config={config}
          controller={matchController}
          localPlayer={LOCAL}
          onRematch={onRematch ?? (() => {})}
          onExit={onExit ?? (() => {})}
        />
      ) : null}

      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        matchExtras={
          <>
            <CopyReplayButton getReplay={() => controller.replayJson()} />
            <Button
              variant="danger"
              size="small"
              block
              onClick={() => {
                setSettingsOpen(false);
                if (window.confirm('Concede this match?')) send({ type: 'Concede', player: LOCAL });
              }}
            >
              Concede
            </Button>
            {onExit ? (
              <Button variant="ghost" size="small" block onClick={onExit}>
                Leave match
              </Button>
            ) : null}
            {settingsExtras}
          </>
        }
      />
    </div>
  );
}

/**
 * Zoom behaviour. UI_UX section 7: hover for 400ms or right-click on desktop,
 * tap on mobile. Hovering opens after a beat so a pointer crossing the hand
 * does not strobe the panel; right-click or a log click pins it until the
 * player closes it. A pinned card is not replaced by an idle hover.
 */
function useHoverZoom() {
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `dismissHover` is handed to callbacks that outlive a render, so it reads
  // the pin through a ref rather than closing over a stale boolean.
  const pinnedRef = useRef(false);
  pinnedRef.current = pinned;

  const cancelTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => cancelTimer, []);

  const handlers: ZoomHandlers = {
    onEnter: (id) => {
      if (pinned) return;
      cancelTimer();
      timer.current = setTimeout(() => setInstanceId(id), HOVER_ZOOM_MS);
    },
    onLeave: () => {
      cancelTimer();
      if (!pinned) setInstanceId(null);
    },
    onPin: (id) => {
      cancelTimer();
      setPinned(true);
      setInstanceId(id);
    },
  };

  return {
    instanceId,
    handlers,
    pin: (id: string) => {
      cancelTimer();
      setPinned(true);
      setInstanceId(id);
    },
    /** Close a hover-opened panel. A pinned one stays until it is closed. */
    dismissHover: () => {
      cancelTimer();
      if (!pinnedRef.current) setInstanceId(null);
    },
    clear: () => {
      cancelTimer();
      setPinned(false);
      setInstanceId(null);
    },
  };
}

const HOVER_ZOOM_MS = 400;

/** A small banner that says what the current targeting step wants. */
function TargetingHud({
  step,
  picked,
  onSkip,
  onCancel,
  name,
}: {
  step: TargetStep;
  picked: string[];
  onSkip: () => void;
  onCancel: () => void;
  name: string;
}) {
  return (
    <div className={styles.abilityMenu} style={{ left: '50%', top: 16, transform: 'translateX(-50%)' }} role="status">
      <span className={styles.abilityLabel}>{name}</span>
      <span className={styles.abilityDetail}>
        {step.label}. {picked.length} of {Math.min(step.count, step.legal.length)} picked.
      </span>
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        {step.optional || picked.length > 0 ? (
          <Button size="small" variant="secondary" onClick={onSkip}>
            {picked.length > 0 ? 'Continue' : 'Skip'}
          </Button>
        ) : null}
        <Button size="small" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Convenience wrapper: the standard bot match, wired to routing. */
export function BotMatch({ config }: { config: MatchConfig }) {
  const navigate = useNavigate();
  const [nonce, setNonce] = useState(0);
  const settings = useSettings();

  return (
    <Battlefield
      key={nonce}
      config={{ ...config, seed: config.seed + nonce, botDelayMs: botDelay(settings) }}
      onRematch={() => setNonce((n) => n + 1)}
      onExit={() => navigate('/play')}
    />
  );
}

export type { GameState };
